import { ChangeDetectionStrategy, Component, Input, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '@/environments/environment';

import { PUBLIC_FOOTER_DOCS } from '@/app/layouts/public-layout/footer/public-footer.links';
import { TabBarComponent } from '@/app/shared/components/tab-bar/tab-bar';
import { TabBarItem } from '@/app/shared/components/tab-bar/tab-bar.types';
import { CartStorageService } from '@/app/features/shared/cart/services/cart-storage.service';
import { PublicConfigService } from '@/app/core/config/public-config.service';
import { PrivateNavbarFacade } from '@/app/layouts/private-layout/navbar/private-navbar.facade';

@Component({
  selector: 'app-public-footer',
  standalone: true,
  imports: [CommonModule, TranslateModule, TabBarComponent],
  templateUrl: './footer.html',
  styleUrl: './footer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicFooterComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly cartStorageService = inject(CartStorageService);
  private readonly navbarFacade = inject(PrivateNavbarFacade);
  readonly publicConfig = inject(PublicConfigService);
  private langSub?: Subscription;

  @Input() variant: 'public' | 'private' = 'public';

  readonly docs = PUBLIC_FOOTER_DOCS;
  readonly currentYear = new Date().getFullYear();

  readonly tiendaUrl = environment.externalApps.tienda;

  tabItems: TabBarItem[] = [];

  ngOnInit(): void {
    this.publicConfig.load();
    this.buildTabItems();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.buildTabItems();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private buildTabItems(): void {
    const isPrivate = this.variant === 'private';

    const items: TabBarItem[] = [];

    if (!isPrivate) {
      items.push({
        type: 'link-internal',
        label: this.translate.instant('FOOTER.HOME'),
        icon: 'footer/Home.svg',
        route: '/',
        exactMatch: true,
      });
    }

    items.push(

      {
        type: 'link-internal',
        label: this.translate.instant('FOOTER.LOCKER'),
        icon: 'footer/Locker.svg',
        route: '/locker',
      },
      {
        type: 'link-internal',
        label: this.translate.instant('FOOTER.STORE'),
        icon: 'footer/Shop.svg',
        route: '/store',
      },
      {
        type: 'link-internal',
        label: this.translate.instant('FOOTER.CART'),
        icon: 'footer/Cart.svg',
        route: '/cart',
        exactMatch: true,
        badgeCount: this.cartStorageService.cartItemCount,
      },
    );

    items.push({
      type: 'button',
      label: this.translate.instant('FOOTER.MENU'),
      icon: 'footer/Menu.svg',
      action: () => this.navbarFacade.openMenu(),
    });

    this.tabItems = items;
  }
}